import React, { useEffect, useMemo, useState } from 'react';
import Rest from 'tools/rest';
import { Table, Row, Col, Form, Input, Modal, Typography, BackTop } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { parseISO as _parseISO, format } from 'date-fns';
import { debounce } from 'lodash';

import Preloader from 'components/preloader';
import PagePartCard from 'components/page_part_card';

const { Text } = Typography;

const PageParts = () => {
  const readQueryState = () => {
    const query = new URLSearchParams(window.location.search);

    return {
      meta: {
        page: Number(query.get('page')) || 1,
        per: Number(query.get('per')) || 20,
        total: 0,
      },
      search: {
        id: query.get('id') || null,
        name: query.get('name') || null,
        content: query.get('content') || null,
      },
    };
  };

  const initialQueryState = readQueryState();

  const [pageParts, setPageParts] = useState([]);
  const [meta, setMeta] = useState(initialQueryState.meta);
  const [search, setSearch] = useState(initialQueryState.search);
  const [loading, setLoading] = useState(false);
  const [visibleCard, setVisibleCard] = useState(false);
  const [pagePart, setPagePart] = useState(null);
  const [dataRelevance, setDataRelevance] = useState(null);

  const loadPageParts = () => {
    const params = {
      page: meta.page,
      per: meta.per,
      search,
    };

    setLoading(true);
    Rest.get('/api/v1/page_parts.json', { params }).then((response) => {
      const { page_parts: loadedPageParts, meta: loadedMeta } = response.data;
      setPageParts(loadedPageParts || []);
      setMeta((prev) => ({
        ...prev,
        page: loadedMeta?.page || prev.page,
        per: loadedMeta?.per || prev.per,
        total: loadedMeta?.total || 0,
      }));
    }).catch((e) => {
      console.error('error', e);
    }).finally(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    loadPageParts();
  }, [meta.page, meta.per, search, dataRelevance]);

  const debouncedSearchChange = useMemo(() => {
    return debounce((name, value) => {
      setMeta((prev) => ({ ...prev, page: 1 }));
      setSearch((prev) => ({ ...prev, [name]: value }));
    }, 400);
  }, []);

  useEffect(() => {
    return () => {
      debouncedSearchChange.cancel();
    };
  }, [debouncedSearchChange]);

  useEffect(() => {
    const query = new URLSearchParams();

    Object.entries(search).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        query.set(key, value);
      }
    });

    query.set('page', String(meta.page));
    query.set('per', String(meta.per));

    const nextUrl = `${window.location.pathname}?${query.toString()}`;
    window.history.replaceState(null, '', nextUrl);
  }, [search, meta.page, meta.per]);

  const handleChangeText = (e) => {
    const { name, value } = e.target;
    debouncedSearchChange(name, value || null);
  };

  const handleTableChange = (pagination) => {
    setMeta((prev) => ({
      ...prev,
      page: pagination.current,
      per: pagination.pageSize,
    }));
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 90,
    },
    {
      title: 'Ключ',
      dataIndex: 'name',
      key: 'name',
      width: 250,
    },
    {
      title: 'Тип',
      dataIndex: 'content_type',
      key: 'content_type',
      width: 180,
      render: (value) => value || '-',
    },
    {
      title: 'Контент',
      dataIndex: 'content',
      key: 'content',
      render: (value) => (value ? `${value.substring(0, 120)}${value.length > 120 ? '...' : ''}` : '-'),
    },
    {
      title: 'Обновлена',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: (value) => (value ? format(_parseISO(value), 'dd.MM.yyyy HH:mm') : null),
    },
  ];

  const pagination = {
    current: meta.page,
    pageSize: meta.per,
    total: meta.total,
    position: ['bottomCenter'],
    showSizeChanger: true,
  };

  return (
    <React.Fragment>
      <BackTop />
      <PageHeader title="Реестр частей страниц" />
      <Form
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
      >
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="ID:"
              style={{ marginBottom: '5px' }}
            >
              <Input
                name="id"
                defaultValue={search.id}
                placeholder="Поиск по id"
                onChange={handleChangeText}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Ключ:"
              style={{ marginBottom: '5px' }}
            >
              <Input
                name="name"
                defaultValue={search.name}
                placeholder="Поиск по ключу"
                onChange={handleChangeText}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Контент:"
              style={{ marginBottom: '5px' }}
            >
              <Input
                name="content"
                defaultValue={search.content}
                placeholder="Поиск по HTML"
                onChange={handleChangeText}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Preloader loading={loading}>
        <Row gutter={20}>
          <Col style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
            <Text>
              Общее кол-во: {meta.total} шт.
            </Text>
          </Col>
        </Row>
        <Table
          style={{ marginTop: '10px' }}
          rowKey={(record) => record.id}
          loading={loading}
          columns={columns}
          dataSource={pageParts}
          onChange={handleTableChange}
          pagination={pagination}
          onRow={(record) => ({
            onClick: () => {
              setPagePart(record);
              setVisibleCard(true);
            },
          })}
        />
      </Preloader>
      {visibleCard && (
        <Modal
          title={`Часть страницы № ${pagePart?.id}`}
          visible={visibleCard}
          onCancel={() => setVisibleCard(false)}
          onOk={() => setVisibleCard(false)}
          footer={false}
          width="70%"
        >
          <PagePartCard
            pagePartId={pagePart?.id}
            onUpdated={() => {
              setVisibleCard(false);
              setPagePart(null);
              setDataRelevance(new Date());
            }}
          />
        </Modal>
      )}
    </React.Fragment>
  );
};

export default PageParts;
